import { PublishedCategoriesResponse } from 'node_modules/@nxt-costco-com/forge-digital-components/dist/utils/contentstack/publishedCategories/types';
import { FeatureFlagState } from 'node_modules/@nxt-costco-com/forge-digital-components/dist/utils/featureFlag';

import { FACET_CS_DEV_KEY } from '@/components/SearchResultsFacets/constants';
import {
    GRS_BRANDS_FACET_KEY,
    GRS_RATING_FACET_KEY,
} from '@/components/SearchResultsFacets/grs.constants';
import { isGRSPriceFacetKey } from '@/components/SearchResultsFacets/grs.lib';
import { FacetNormalized } from '@/components/SearchResultsFacets/types';
import { SEARCH_V2_FEATURE_TOGGLE } from '@/constants/contentStack';
import {
    GRS_FACETS_CONFIG_KEY,
    GRS_KEYWORD_SEARCH_SERVICE_CONFIG_KEY,
    GRS_PROVIDER_BETA_RELEASE_FEATURE_TOGGLE_CONFIG_DEVELOPMENT_KEY,
    GRS_PROVIDER_FEATURE_TOGGLE_CONFIG_DEVELOPMENT_KEY,
    GRS_PROVIDER_PHASED_RELEASE_FEATURE_TOGGLE_CONFIG_DEVELOPMENT_KEY,
} from '@/constants/grs';
import { CONTENT_STACK_DEV_KEY } from '@/constants/index';
import {
    getConfigurationSettingByConfigKey,
    getConfigurationSettingByDevelopmentKey,
} from '@/services/content/config.lib';
import {
    findByEntryId,
    getConfigurationSettingByDeveloperKey,
} from '@/services/content/data';
import { AppName } from '@/services/content/featureFlag';
import type {
    AccordionSetEntry,
    AdBuilderReference,
    AdSetCostcoReference,
    AdSetThirdPartyReference,
    AdTargetPlacementBlockReference,
    BrandedSearchBlock,
    BrandedSearchReference,
    BulletDetailReference,
    ButtonSetReference,
    CategoryLandingPage,
    ComposerData,
    ContentGroup,
    CriteoInGridBlockReference,
    CustomRichTextBlockReference,
    FeatureHighlightCardV2Reference,
    GRSFacetConfigProperties,
    GrsConfig,
    MarkdownReference,
    ProgramCardReference,
    SearchComposerData,
    SearchComposerV2Data,
    SearchPageConfigProperties,
    SearchRuleEntry,
    SearchServiceConfig,
    SegmentBasedEntryBlockReference,
    SiteSettingsContentStackData,
    TabsBlockReference,
    TextBuilderReference,
    TextSectionBlock,
    TieredOfferCardReference,
} from '@/types/contentStack';
import {
    type ContentStackEntryDataProps,
    ContentType,
    type LocaleProps,
    fetchPublishedCategories,
    getCriteoContentStackConfigurationEntries,
    getFeatureFlagState,
} from '@nxt-costco-com/forge-digital-components';

import {
    ENABLED_WAPO_WAREHOUSES,
    ENABLE_MULTI_VARIANT_AVAILABILITY_MESSAGE,
    FIRST_ITEM_INDEX_TOKEN,
    IS_WAPO_ENABLED,
    LAST_ITEM_INDEX_TOKEN,
    TOTAL_ITEMS_TOKEN,
} from './constants';

export function isAccordionSetEntry(
    entry: ContentGroup,
): entry is AccordionSetEntry {
    return Boolean(entry && 'accordion_set_block' in entry);
}

export function isAdBuilderEntry(
    entry: ContentGroup,
): entry is AdBuilderReference {
    return 'ad_builder_block' in entry;
}

export function isAdSetCostcoEntry(
    entry: ContentGroup,
): entry is AdSetCostcoReference {
    return 'ad_set_costco_block' in entry;
}

export function isAdSetThirdPartyEntry(
    entry: ContentGroup,
): entry is AdSetThirdPartyReference {
    return 'ad_set_3rd_party_block' in entry;
}

export function isCriteoInGridBannerEntry(
    entry: ContentGroup,
): entry is CriteoInGridBlockReference {
    return 'criteo_ingrid_ad_block' in entry;
}

export function isCustomRichTextBlock(
    entry: ContentGroup,
): entry is CustomRichTextBlockReference {
    return 'custom_rich_text_block' in entry;
}

export function isButtonEntry(
    entry: ContentGroup,
): entry is ButtonSetReference {
    return 'button_set_block' in entry;
}

export function isMarkdownEntry(
    entry: ContentGroup,
): entry is MarkdownReference {
    return 'markdown_block' in entry;
}

export function isBulletDetailEntry(
    entry: ContentGroup,
): entry is BulletDetailReference {
    return 'bullet_detail_card_block' in entry;
}

export function isProgramCardEntry(
    entry: ContentGroup,
): entry is ProgramCardReference {
    return 'program_card_block' in entry;
}

export function isFeatureHighlightCardV2Entry(
    entry: ContentGroup,
): entry is FeatureHighlightCardV2Reference {
    return 'feature_highlight_card_v2_block' in entry;
}

export function isTieredOfferCardEntry(
    entry: ContentGroup,
): entry is TieredOfferCardReference {
    return 'tiered_offer_card_block' in entry;
}

export function isTextBuilderEntry(
    entry: ContentGroup,
): entry is TextBuilderReference {
    return 'text_builder_block' in entry;
}

export function isTextSectionEntry(
    entry: ContentGroup,
): entry is TextSectionBlock {
    return 'text_section_block' in entry;
}
export function isBrandedSearchEntry(
    entry: ContentGroup,
): entry is BrandedSearchReference {
    return 'branded_search_block' in entry;
}

export function isSegmentBasedEntry(
    entry: ContentGroup,
): entry is SegmentBasedEntryBlockReference {
    return 'segment_based_entry_block' in entry;
}

export function isBrandedSearchCLPEntry(
    entry: ContentGroup,
): entry is BrandedSearchBlock {
    return 'branded_search' in entry;
}

export function getDisplayGroupId(entry: ContentGroup) {
    if (isAccordionSetEntry(entry)) {
        return entry.accordion_set_block?.accordion_set_ref?.[0]?.uid;
    }
    if (isAdBuilderEntry(entry)) {
        return entry.ad_builder_block.ad_builder_ref?.[0]?.uid;
    }
    if (isAdSetCostcoEntry(entry)) {
        return entry.ad_set_costco_block.ad_set_costco_ref?.[0]?.uid;
    }
    if (isAdSetThirdPartyEntry(entry)) {
        return entry.ad_set_3rd_party_block.ad_set_3rd_party_ref?.[0]?.uid;
    }
    if (isCriteoInGridBannerEntry(entry)) {
        return entry.criteo_ingrid_ad_block?.ingrid_type_ref;
    }
    if (isBulletDetailEntry(entry)) {
        return entry.bullet_detail_card_block.bullet_detail_card_ref?.[0]?.uid;
    }
    if (isButtonEntry(entry)) {
        return entry.button_set_block.button_set_ref?.[0]?.uid;
    }
    if (isCustomRichTextBlock(entry)) {
        return entry.custom_rich_text_block.custom_rich_text_ref?.[0]?.uid;
    }
    if (isFeatureHighlightCardV2Entry(entry)) {
        return entry.feature_highlight_card_v2_block
            .feature_highlight_card_v2_ref?.[0]?.uid;
    }
    if (isTieredOfferCardEntry(entry)) {
        return entry.tiered_offer_card_block.tiered_offer_card_ref?.[0]?.uid;
    }
    if (isMarkdownEntry(entry)) {
        return entry.markdown_block._metadata?.uid;
    }
    if (isProgramCardEntry(entry)) {
        return entry.program_card_block.program_card_ref?.[0]?.uid;
    }
    if (isTextBuilderEntry(entry)) {
        return entry.text_builder_block.text_builder_ref?.[0]?.uid;
    }
    if (isTabsBlockEntry(entry)) {
        return entry.tabs_block.tabs_ref?.[0]?.uid;
    }
    if (isTextSectionEntry(entry)) {
        return entry.text_section_block?._metadata?.uid;
    }
    if (isBrandedSearchEntry(entry)) {
        return entry.branded_search_block?.branded_search_ref?.[0]?.uid;
    }
    if (isSegmentBasedEntry(entry)) {
        return entry.segment_based_entry_block?.segment_based_entry_ref?.[0]
            ?.uid;
    }
    if (isBrandedSearchCLPEntry(entry)) {
        return entry.branded_search?._metadata?.uid;
    }
}

export function getDisplayGroupContentType(entry: AdSetThirdPartyReference) {
    if (isAdSetThirdPartyEntry(entry)) {
        return entry.ad_set_3rd_party_block?.ad_set_3rd_party_ref?.[0]
            ?._content_type_uid;
    }
}

export function isTabsBlockEntry(
    entry: ContentGroup,
): entry is TabsBlockReference {
    if (!entry) {
        return false;
    }
    return 'tabs_block' in entry;
}

export function isSearchRuleEntry(
    entry?: SearchRuleEntry | CategoryLandingPage,
): entry is SearchRuleEntry {
    if (!entry) {
        return false;
    }

    return 'search_page_title' in entry;
}

export function isCategoryLandingPageEntry(
    entry?: SearchRuleEntry | CategoryLandingPage,
): entry is CategoryLandingPage {
    if (!entry) {
        return false;
    }

    return 'page_id' in entry && 'category_id' in entry;
}

export function populateProductCount(
    template: string | undefined,
    data: {
        firstDisplayedItemIndex: number;
        lastDisplayedItemIndex: number;
        totalItems: number;
    },
) {
    if (!template) {
        return null;
    }
    return template
        .replace(
            FIRST_ITEM_INDEX_TOKEN,
            data.firstDisplayedItemIndex.toLocaleString(),
        )
        .replace(
            LAST_ITEM_INDEX_TOKEN,
            data.lastDisplayedItemIndex.toLocaleString(),
        )
        .replace(TOTAL_ITEMS_TOKEN, data.totalItems.toLocaleString());
}

export function getFirstItemIndex(currentPage: number, resultsPerPage: number) {
    return (currentPage - 1) * resultsPerPage + 1;
}

export function getLastItemIndex(
    currentPage: number,
    resultsPerPage: number,
    totalDocs: number,
) {
    let lastDisplayedItemIndex = currentPage * resultsPerPage;

    if (lastDisplayedItemIndex > totalDocs) {
        lastDisplayedItemIndex = totalDocs;
    }

    return lastDisplayedItemIndex;
}

export function isTopLevelContentGroup(
    entryData: SearchComposerData | SearchComposerV2Data | CategoryLandingPage,
): entryData is SearchComposerV2Data | CategoryLandingPage {
    // If it has the additional property of ad_placements, its an v1 search composer
    return !(
        entryData.above_search_results.hasOwnProperty('ad_placements') ||
        entryData.below_search_results.hasOwnProperty('ad_placements') ||
        entryData.ingrid_search_results.hasOwnProperty('ad_placements')
    );
}

/**
 * This takes in multiple page structures from ContentStack and returns
 * a common structure that can be used to render the page ads
 */
export function getComposerDataFromEntry(
    entryData?: SearchComposerData | SearchComposerV2Data | CategoryLandingPage,
): ComposerData {
    if (!entryData) {
        return {
            aboveGridPlacements: [],
            belowGridPlacements: [],
            inGridPlacements: [],
        };
    }

    function extractContentGroupArray(
        group:
            | 'above_search_results'
            | 'below_search_results'
            | 'ingrid_search_results',
    ): ContentGroup[] {
        if (!entryData) {
            return [];
        }

        if (isTopLevelContentGroup(entryData)) {
            // SearchComposerV2 & CategoryLandingPages
            return entryData[group];
        }
        // SearchComposer V1
        return entryData[group]?.ad_placements || [];
    }

    // Support for v1 and v2 SearchComposer as well as CLP (same as V2)
    const aboveGridPlacements = extractContentGroupArray(
        'above_search_results',
    );

    const belowGridPlacements = extractContentGroupArray(
        'below_search_results',
    );

    const inGridPlacements = extractContentGroupArray('ingrid_search_results');

    return { aboveGridPlacements, belowGridPlacements, inGridPlacements };
}

export function isAdditionalAdsZone(
    adPlacement: ContentGroup,
): adPlacement is AdTargetPlacementBlockReference {
    return 'ad_targeting_placement_block' in adPlacement;
}

// Takes the baseEntry object, and looks in each of the three content group arrays for targeted ad zones,
// if found, it will inject the appliedEntries ad for that content group into the zone
export function mergeComposerData(
    baseEntry: ComposerData,
    appliedEntry?: ComposerData,
) {
    if (!appliedEntry) {
        return baseEntry;
    }

    const localAppliedEntry = structuredClone(appliedEntry);

    const mapSpecificAdsIntoTargetZones =
        (group: keyof ComposerData) => (adPlacement: ContentGroup) => {
            // It's not a ad zone, or it is not set to enabled, keep it as is
            if (
                !isAdditionalAdsZone(adPlacement) ||
                !adPlacement.ad_targeting_placement_block.enable_targeted_ads
            ) {
                return adPlacement;
            }
            // Returning the splice lets us clear out the ads from the applied rule
            // so we don't worry about adding it to multiple ad zones
            return localAppliedEntry[group].splice(
                0,
                localAppliedEntry[group].length,
            );
        };

    // Iterate over each ad placement to look for the first targeting placement, and inject
    // all above grid ad placements from the applied rule into the base rule
    const aboveGridPlacements = baseEntry.aboveGridPlacements
        .map(mapSpecificAdsIntoTargetZones('aboveGridPlacements'))
        .flat();

    const belowGridPlacements = baseEntry.belowGridPlacements
        .map(mapSpecificAdsIntoTargetZones('belowGridPlacements'))
        .flat();

    const inGridPlacements = baseEntry.inGridPlacements
        .map(mapSpecificAdsIntoTargetZones('inGridPlacements'))
        .flat();

    return { aboveGridPlacements, belowGridPlacements, inGridPlacements };
}

export function _createCriteoFilterMap(
    refine: string,
): Record<string, string[]> {
    return refine?.split('||')?.reduce(
        (filterObj, filterString) => {
            // Ignore empty strings
            if (!filterString.trim()) return filterObj;

            // Match "key_name-someValue"
            const match = filterString.match(/^([^-\s]+)-(.+)$/);
            if (!match) return filterObj;

            const key = match[1]?.toLowerCase();
            const value = match[2];

            if (key && value) {
                filterObj[key] = filterObj[key] || [];
                filterObj[key].push(value);
            }

            return filterObj;
        },
        {} as Record<string, string[]>,
    );
}

export function createCriteoSearchFiltersStringFromGRSFilters(
    grsFilters: FacetNormalized[],
    categoryId?: string,
) {
    const filterStrings = grsFilters.map(facet => {
        const values = facet.buckets.map(bucket => bucket.val).filter(Boolean);

        // Special handling for certain facets
        if (isGRSPriceFacetKey(facet.facetKey)) {
            const priceValues = facet.buckets.map(({ low, high }) => {
                const rangeString = [low, high]
                    .filter(val => typeof val === 'number')
                    .join('-');

                if (rangeString) {
                    return `[${rangeString}]`;
                }

                return '';
            });

            if (priceValues.length === 0) {
                return '';
            }

            return `(price_range,in,${priceValues.join(',')})`;
        }

        if (facet.facetKey === GRS_RATING_FACET_KEY) {
            const smallestValue = facet.buckets.reduce(
                (smallestValue, bucket) => {
                    const { val } = bucket; // val is a string or boolean
                    const valAsString = String(val);
                    if (
                        !smallestValue ||
                        parseInt(valAsString) < parseInt(smallestValue)
                    ) {
                        return valAsString;
                    }

                    return smallestValue;
                },
                undefined as string | undefined,
            );

            // If we have a smallest value, we create the filter string
            if (smallestValue !== undefined) {
                return `(ratings,ge,${smallestValue})`;
            } else {
                return '';
            }
        }

        if (facet.facetKey === GRS_BRANDS_FACET_KEY) {
            if (values?.length > 0) {
                return `(brand,in,${values.join(',')})`;
            }

            return '';
        }

        // Default for non-special facets
        if (values.length > 0) {
            return `(${facet.facetKey},in,${values.join(',')})`;
        }

        return '';
    });

    if (categoryId) {
        filterStrings.push(`(category,in,${categoryId})`);
    }

    return filterStrings.filter(s => !!s).join(', ');
}

export function createCriteoSearchFiltersString(
    refine: string | string[] = '',
    categoryId?: string,
) {
    if (Array.isArray(refine)) {
        refine = refine[0] as string;
    }

    if (categoryId) {
        // show relevant products, applicable when keyword search on a category page /category?keyword=costco
        refine = refine + `||category-${categoryId}`;
    }

    if (!refine) {
        // If refine is empty, return an empty string
        return '';
    }

    const refineFilterMap = _createCriteoFilterMap(refine);

    const filterStrings = Object.keys(refineFilterMap).reduce((acc, key) => {
        const values = refineFilterMap[key] || [];

        if (key === 'item_rating_value') {
            const smallestValue = values?.reduce(
                (smallestValue, value) => {
                    if (
                        !smallestValue ||
                        parseInt(value) < parseInt(smallestValue)
                    ) {
                        return value;
                    }

                    return smallestValue;
                },
                undefined as string | undefined,
            );

            // If we have a smallest value, we create the filter string
            if (smallestValue !== undefined) {
                acc.push(`(ratings,ge,${smallestValue})`);
            }
        } else if (key === 'item_location_pricing_saleprice') {
            const filterValues = values.reduce((acc, value) => {
                if (!value) return acc;

                const updatedValue = value?.replace(/\s*TO\s*/, '-');
                acc.push(updatedValue);

                return acc;
            }, [] as string[]);

            if (filterValues.length > 0) {
                acc.push(`(price_range,in,${filterValues.join(',')})`);
            }
        } else if (key === 'brand_attr') {
            if (values?.length > 0) {
                acc.push(`(brand,in,${values.join(',')})`);
            }
        } else if (key === 'category') {
            if (categoryId) {
                acc.push(`(category,in,${categoryId})`);
            }
        } else {
            // For any other keys, we can just join the values with a comma
            if (values?.length > 0) {
                acc.push(`(${key},in,${values.join(',')})`);
            }
        }

        return acc;
    }, [] as string[]);

    return filterStrings.filter(s => !!s).join(', ');
}

export async function getCLPComposerDataFromEntry(
    entryId: string,
    lang: LocaleProps,
): Promise<ComposerData | undefined> {
    const categoryLandingPageEntry = await findByEntryId<
        CategoryLandingPage | undefined
    >(lang, ContentType.CategoryLandingPage, entryId);

    return getComposerDataFromEntry(categoryLandingPageEntry);
}

export const getPageConfigs = async ({
    lang,
    enableLivePreview,
}: {
    lang: LocaleProps;
    enableLivePreview?: boolean;
}) => {
    // Placeholder for future page config fetching logic
    let siteConfig: SiteSettingsContentStackData | undefined;
    let pageConfig:
        | ContentStackEntryDataProps<SearchPageConfigProperties>
        | undefined;
    let facetConfig: ContentStackEntryDataProps | undefined;
    let serviceConfig: SearchServiceConfig | undefined;
    let isGRSFeatureFlagEnabled: boolean = false;
    let isGRSBetaReleaseFeatureFlagEnabled = false;
    let isGRSPhasedReleaseFeatureFlagEnabled = false;
    let showMultiVariantsAvailability: boolean = false;
    let isWAPOEnabled: boolean = false;
    let isSearchModelV2FeatureFlagEnabled: boolean = false;
    let grsFacetConfig:
        | ContentStackEntryDataProps<GRSFacetConfigProperties>
        | undefined;
    let grsApiServiceConfig: GrsConfig | undefined;
    let publishedCategories: PublishedCategoriesResponse | undefined;
    let criteoServiceConfig;
    let wapoEnabledWarehouses: string[] = [];

    const appName = (process.env.APPNAME ||
        process.env.NEXT_PUBLIC_APPNAME) as AppName;

    try {
        const [
            _siteConfig,
            _pageConfig,
            searchFacetConfig,
            isGRSFeatureFlagEnabledObj,
            enableMultiVariantAvailabilityObj,
            isSearchModelV2FeatureFlagEnabledObj,
            _publishedCategories,
            _criteoServiceConfig,
            isWAPOEnabledObj,
        ] = await Promise.all([
            getConfigurationSettingByDeveloperKey<SiteSettingsContentStackData>(
                ContentType.SiteSettings,
                'site_settings',
                lang,
                enableLivePreview,
            ),
            getConfigurationSettingByDeveloperKey<
                ContentStackEntryDataProps<SearchPageConfigProperties>
            >(
                ContentType.PageSettings,
                CONTENT_STACK_DEV_KEY,
                lang,
                enableLivePreview,
            ),
            getConfigurationSettingByDeveloperKey<ContentStackEntryDataProps>(
                ContentType.ModuleSettings,
                FACET_CS_DEV_KEY,
                lang,
                enableLivePreview,
            ),
            getFeatureFlagState(
                GRS_PROVIDER_FEATURE_TOGGLE_CONFIG_DEVELOPMENT_KEY,
                appName,
            ),
            getFeatureFlagState(
                ENABLE_MULTI_VARIANT_AVAILABILITY_MESSAGE,
                appName,
            ),
            getFeatureFlagState(SEARCH_V2_FEATURE_TOGGLE, appName),
            fetchPublishedCategories(lang),
            getCriteoContentStackConfigurationEntries(lang),
            getFeatureFlagState(IS_WAPO_ENABLED, appName),
        ]);

        pageConfig = _pageConfig;
        facetConfig = searchFacetConfig;
        isGRSFeatureFlagEnabled =
            isGRSFeatureFlagEnabledObj?.isEnabled || false;
        showMultiVariantsAvailability =
            enableMultiVariantAvailabilityObj?.isEnabled || false;
        isWAPOEnabled = isWAPOEnabledObj?.isEnabled || false;
        isSearchModelV2FeatureFlagEnabled =
            isSearchModelV2FeatureFlagEnabledObj?.isEnabled || false;
        publishedCategories = _publishedCategories;
        siteConfig = _siteConfig;
        criteoServiceConfig = _criteoServiceConfig;

        const promisesConfigSecondSet = [];

        // If config has a service config key, fetch it
        if (pageConfig?.properties?.[0]?.configKey) {
            const lucidworksConfigKey = pageConfig.properties[0].configKey;

            // The Lucidworks service config is optional and being retired. Settle
            // it on its own so a deleted entry cannot reject the Promise.all below
            // and discard the GRS configs fetched alongside it.
            promisesConfigSecondSet.push(
                getConfigurationSettingByDevelopmentKey<SearchServiceConfig>(
                    ContentType.ServiceConfiguration,
                    lucidworksConfigKey,
                    enableLivePreview,
                ).catch(err => {
                    console.error(
                        `Lucidworks service config '${lucidworksConfigKey}' could not be fetched; continuing without it:`,
                        err,
                    );

                    return undefined;
                }),
            );
        } else {
            promisesConfigSecondSet.push(Promise.resolve(undefined));
        }

        if (isGRSFeatureFlagEnabled) {
            promisesConfigSecondSet.push(
                getFeatureFlagState(
                    GRS_PROVIDER_BETA_RELEASE_FEATURE_TOGGLE_CONFIG_DEVELOPMENT_KEY,
                    appName,
                ),
            );
            promisesConfigSecondSet.push(
                getFeatureFlagState(
                    GRS_PROVIDER_PHASED_RELEASE_FEATURE_TOGGLE_CONFIG_DEVELOPMENT_KEY,
                    appName,
                ),
            );
            promisesConfigSecondSet.push(
                getConfigurationSettingByDeveloperKey<
                    ContentStackEntryDataProps<GRSFacetConfigProperties>
                >(
                    ContentType.ModuleSettings,
                    GRS_FACETS_CONFIG_KEY,
                    lang,
                    enableLivePreview,
                ),
            );
            promisesConfigSecondSet.push(
                getConfigurationSettingByDevelopmentKey<GrsConfig>(
                    ContentType.ServiceConfiguration,
                    GRS_KEYWORD_SEARCH_SERVICE_CONFIG_KEY,
                ),
            );
        } else {
            promisesConfigSecondSet.push(Promise.resolve(undefined));
            promisesConfigSecondSet.push(Promise.resolve(undefined));
            promisesConfigSecondSet.push(Promise.resolve(undefined));
            promisesConfigSecondSet.push(Promise.resolve(undefined));
        }

        if (isWAPOEnabled) {
            promisesConfigSecondSet.push(
                getConfigurationSettingByConfigKey<
                    ContentStackEntryDataProps<GRSFacetConfigProperties>
                >(ContentType.ConfigurationSetting, ENABLED_WAPO_WAREHOUSES),
            );
        } else {
            promisesConfigSecondSet.push(Promise.resolve(undefined));
        }

        const [
            fetchedServiceConfig,
            grsBetaReleaseFeatureFlagObj,
            grsPhasedReleaseFeatureFlagObj,
            grsFacetConfigEntry,
            grsKeywordSearchServiceConfig,
            wapoEnabledWarehousesEntry,
        ] = await Promise.all(promisesConfigSecondSet);

        if (fetchedServiceConfig) {
            serviceConfig = fetchedServiceConfig as SearchServiceConfig;
        }
        isGRSBetaReleaseFeatureFlagEnabled =
            (grsBetaReleaseFeatureFlagObj as FeatureFlagState)?.isEnabled ||
            false;
        isGRSPhasedReleaseFeatureFlagEnabled =
            (grsPhasedReleaseFeatureFlagObj as FeatureFlagState)?.isEnabled ||
            false;
        if (grsFacetConfigEntry) {
            grsFacetConfig =
                grsFacetConfigEntry as ContentStackEntryDataProps<GRSFacetConfigProperties>;
        }
        if (grsKeywordSearchServiceConfig) {
            grsApiServiceConfig = grsKeywordSearchServiceConfig as GrsConfig;
        }
        if (wapoEnabledWarehousesEntry) {
            const siteType = process.env.SITE?.toLowerCase()?.substring(0, 2);
            wapoEnabledWarehouses =
                // @ts-expect-error TODO: Fix ts-expect-error after exporting correct types from digital components
                wapoEnabledWarehousesEntry?.custom?.enabledWarehousesList?.[
                    siteType
                ] ?? [];
        }
    } catch (err) {
        console.error('Error fetching page configurations:', err);
    }

    return {
        siteConfig,
        pageConfig,
        facetConfig,
        serviceConfig,
        isGRSFeatureFlagEnabled,
        isGRSBetaReleaseFeatureFlagEnabled,
        isGRSPhasedReleaseFeatureFlagEnabled,
        showMultiVariantsAvailability,
        isWAPOEnabled,
        isSearchModelV2FeatureFlagEnabled,
        grsFacetConfig,
        grsApiServiceConfig,
        publishedCategories,
        criteoServiceConfig,
        wapoEnabledWarehouses,
    };
};

export async function getServerSideGrsConfig(): Promise<GrsConfig> {
    const grsConfig = await getConfigurationSettingByDevelopmentKey<GrsConfig>(
        ContentType.ServiceConfiguration,
        GRS_KEYWORD_SEARCH_SERVICE_CONFIG_KEY,
    );
    return grsConfig;
}
export async function getGrsFacetConfig(
    locale: LocaleProps,
): Promise<ContentStackEntryDataProps<GRSFacetConfigProperties>> {
    const grsFacetConfig = await getConfigurationSettingByDeveloperKey<
        ContentStackEntryDataProps<GRSFacetConfigProperties>
    >(ContentType.ModuleSettings, GRS_FACETS_CONFIG_KEY, locale);
    return grsFacetConfig;
}

// Category landing pages could have search enabled or not
export function isSearchEnabled(
    categoryLandingPage: CategoryLandingPage | SearchRuleEntry | undefined,
): boolean {
    if (!categoryLandingPage) {
        return true;
    }

    if ('enable_search' in categoryLandingPage) {
        return categoryLandingPage.enable_search;
    }
    return true;
}

// Category landing pages could have search filters (left column) enabled or not
export function isFilterEnabled(
    categoryLandingPage: CategoryLandingPage | SearchRuleEntry | undefined,
): boolean {
    if (!categoryLandingPage) {
        return true;
    }

    if ('enable_search_filter' in categoryLandingPage) {
        return categoryLandingPage.enable_search_filter;
    }

    return true;
}

// Get site settings for locale
export async function getSiteSettings(
    locale: LocaleProps,
): Promise<SiteSettingsContentStackData> {
    const siteSettings =
        await getConfigurationSettingByDeveloperKey<SiteSettingsContentStackData>(
            ContentType.SiteSettings,
            'site_settings',
            locale,
        );

    return siteSettings;
}
